import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CpfCnpjPipe } from '../shared/pipes/cpf-cnpj.pipe';
import { MatSpinnerOverlayComponent } from '../components/mat-spinner-overlay/mat-spinner-overlay.component';
import { SnackBarService } from '../services/snack-bar-service.service';
import { CadastrarSocioComponent, CadastrarSocioDialogData } from './cadastrar-socio/cadastrar-socio.component';
import { ConfirmarSubmissaoComponent } from './confirmar-submissao/confirmar-submissao.component';
import { HabilitacaoSwapService } from '../services/habilitacao-swap.service';
import { LocalStorageService } from '../services/localstorage.service';
import { KycRequestViewModel } from '../habilitacao-swap/models/kyc.model';
import {
  HabilitacaoSwapModel,
  HistoricoSwapModel,
  SocioModel,
  STATUS_LABELS,
  StatusHabilitacao,
} from './models';

@Component({
  selector: 'app-habilitacao-swap',
  templateUrl: './habilitacao-swap.component.html',
  styleUrls: ['./habilitacao-swap.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatTableModule,
    MatSpinnerOverlayComponent,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatBadgeModule,
    MatTabsModule,
    CpfCnpjPipe,
  ],
})
export class HabilitacaoSwapComponent implements AfterViewInit {
  // ==================== Dependencies ====================
  private readonly dialog = inject(MatDialog);
  private readonly service = inject(HabilitacaoSwapService);
  private readonly snack = inject(SnackBarService);
  private readonly storage = inject(LocalStorageService);

  // ==================== ViewChild ====================
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ==================== State ====================
  public empresa: any | null = null;
  public loading = false;
  public habilitacao: HabilitacaoSwapModel | null = null;
  public historico: HistoricoSwapModel[] = [];
  public selectedTabIndex = 0;

  // ==================== Mock Log Data ====================
  public logs: { dataHora: string; acao: string; usuario: string; detalhe: string }[] = [
    { dataHora: '25/02/2026 15:30', acao: 'ACESSO', usuario: 'admin.user', detalhe: 'Acessou a tela de habilitação SWAP.' },
    { dataHora: '25/02/2026 15:31', acao: 'INCLUSAO', usuario: 'admin.user', detalhe: 'Incluiu sócio João da Silva (CPF: 123.456.789-01).' },
    { dataHora: '25/02/2026 15:32', acao: 'INCLUSAO', usuario: 'admin.user', detalhe: 'Incluiu sócio Empresa ABC Ltda (CNPJ: AB.123.456/0001-99).' },
    { dataHora: '25/02/2026 15:35', acao: 'SUBMISSAO', usuario: 'admin.user', detalhe: 'Submeteu 2 sócio(s) para validação SWAP/Bacen.' },
    { dataHora: '22/02/2026 09:15', acao: 'STATUS', usuario: 'sistema', detalhe: 'Status atualizado para AJUSTE_SOLICITADO.' },
  ];

  // ==================== Data Source ====================
  readonly dataSource = new MatTableDataSource<SocioModel>([]);
  readonly displayedColumns: string[] = [
    'dscNome',
    'dscTipoPessoa',
    'numCpfCnpj',
    'datNascimento',
    'dscNomePai',
    'dscNomeMae',
    'acoes',
  ];

  readonly displayedColumnsReadOnly: string[] = [
    'dscNome',
    'dscTipoPessoa',
    'numCpfCnpj',
    'datNascimento',
    'dscNomePai',
    'dscNomeMae',
  ];

  // ==================== Getters ====================
  get status(): StatusHabilitacao {
    return this.habilitacao?.status || 'INI';
  }

  get statusLabel(): string {
    return this.habilitacao?.statusLabel || STATUS_LABELS['INI'];
  }

  get isReadOnly(): boolean {
    return this.habilitacao?.isReadOnly || false;
  }

  get isEditable(): boolean {
    return this.habilitacao?.isEditable ?? true;
  }

  get socios(): SocioModel[] {
    return this.habilitacao?.socios || [];
  }

  get columns(): string[] {
    return this.isReadOnly ? this.displayedColumnsReadOnly : this.displayedColumns;
  }

  get dataLiberacao(): string {
    return this.habilitacao?.dataLiberacaoFormatada || '';
  }

  get mensagemSwap(): string | null {
    return this.habilitacao?.mensagemSwap || null;
  }

  // ==================== Lifecycle ====================
  constructor() {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  // ==================== Data Loading ====================
  private loadData(): void {
    this.loading = true;

    // Inicializando como objeto literal para evitar erro de construtor
    const payload: KycRequestViewModel = {
      Guid: this.service.usuario?.retorno.guid || '',
      CodTipoEntidade: this.service.usuario?.retorno.tipo
    };

    this.service.buscarHabilitacao(payload).subscribe({
      next: (habilitacao) => {

        if (habilitacao) {
          this.habilitacao = habilitacao;
          this.empresa = habilitacao.empresa;

          this.dataSource.data = habilitacao.socios;
          this.historico = habilitacao.historicos;
        } else {
          this.habilitacao = new HabilitacaoSwapModel(
            null, null, 'INI', null, [], [], null, null
          );
          this.dataSource.data = [];
          this.historico = [];
        }
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage(
          'Erro ao carregar dados de habilitação',
          ['snackbar-error'], 'Fechar', 3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  // ==================== Dialog Methods ====================
  cadastrarSocio(): void {
    const data: CadastrarSocioDialogData = {
      cpfsCnpjsExistentes: this.socios.map((s) => s.numCpfCnpj.replace(/\D/g, '')),
    };
    const dialogRef = this.dialog.open(CadastrarSocioComponent, {
      width: '50%', data,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.sucesso && result.retorno) {
        const novoSocio = SocioModel.fromJson(result.retorno);

        console.log('Novo Socio:', novoSocio);

        if (!this.habilitacao) {
          this.habilitacao = new HabilitacaoSwapModel(null, null, 'INI', null, [], [], null, null);
        }

        // Adiciona à lista local e atualiza o grid
        this.habilitacao.socios.push(novoSocio);
        this.dataSource.data = [...this.habilitacao.socios];

        // Atualiza o histórico (pois o serviço adicionou uma entrada mockada)
        this.service.buscarHistorico().subscribe(h => this.historico = h);

        // Sincroniza com o backend via KYC
        const payload = this.getKycPayload();
        if (payload) {
          this.executeSubmissao(payload, true);
          this.snack.flashMessage('Sócio cadastrado e enviado para processamento!', ['snackbar-success'], 'Fechar', 3000);
        }
      }
    });
  }

  editarSocio(socio: SocioModel): void {
    const data: CadastrarSocioDialogData = {
      socio,
      cpfsCnpjsExistentes: this.socios.map((s) => s.numCpfCnpj.replace(/\D/g, '')),
    };
    const dialogRef = this.dialog.open(CadastrarSocioComponent, {
      width: '50%', data,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.sucesso && result.retorno) {
        const socioEditado = SocioModel.fromJson(result.retorno);

        console.log('Socio Editado:', this.habilitacao?.socios);

        if (this.habilitacao) {
          const index = this.habilitacao.socios.findIndex(s => s.codKycSocio === socioEditado.codKycSocio);
          if (index !== -1) {
            this.habilitacao.socios[index] = socioEditado;
            this.dataSource.data = [...this.habilitacao.socios];

            this.service.buscarHistorico().subscribe(h => this.historico = h);

            // Sincroniza com o backend via KYC
            const payload = this.getKycPayload();
            if (payload) {
              this.executeSubmissao(payload, true);
              this.snack.flashMessage('Sócio atualizado e enviado para processamento!', ['snackbar-success'], 'Fechar', 3000);
            }
          }
        }
      }
    });
  }

  removerSocio(socio: SocioModel): void {
    if (!socio.codKycSocio) return;
    if (!confirm(`Deseja realmente remover o sócio "${socio.dscNome}"?`)) return;

    this.loading = true;
    this.service.removerSocio(socio.codKycSocio).subscribe({
      next: (response) => {
        if (response?.sucesso && this.habilitacao) {
          this.habilitacao.socios = this.habilitacao.socios.filter(s => s.codKycSocio !== socio.codKycSocio);          
          this.dataSource.data = [...this.habilitacao.socios];

          const payload = this.getKycPayload();

          if (payload) {
            this.executeSubmissao(payload, true);
            this.snack.flashMessage('Sócio removido e dados atualizados!', ['snackbar-success'], 'Fechar', 3000);
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage('Erro ao remover sócio', ['snackbar-error'], 'Fechar', 3000);
      },
    });
  }

  // ==================== Submissão ====================
  private getKycPayload(): KycRequestViewModel | null {
    const userDataStr = this.storage.getItem('userData');
    const userData = userDataStr ? JSON.parse(userDataStr) : null;

    if (!userData) return null;

    let retorno = {
      ...this.empresa,
      codAcesso: userData.retorno.codigoAcesso,
      codTipoEntidade: userData.retorno.tipo,
      parceria: 'SWAP',
      socios: this.socios.map(s => ({
        dscTipoPessoa: s.dscTipoPessoa,
        numCpfCnpj: s.numCpfCnpj.replace(/\D/g, ''),
        dscNome: s.dscNome,
        datNascimento: s.dscTipoPessoa === "CPF" ? s.datNascimento : null,
        dscNomePai: s.dscTipoPessoa === "CPF" ? s.dscNomePai : null,
        dscNomeMae: s.dscTipoPessoa === "CPF" ? s.dscNomeMae : null,
        datFundacao: s.dscTipoPessoa === "CNPJ" ? s.datFundacao : null,
        dscNomeReduzido: s.dscTipoPessoa === "CNPJ" ? s.dscNomeReduzido : null,
      })),
      guid: userData.retorno.guid
    } 
    return retorno as KycRequestViewModel;
  }

  private executeSubmissao(payload: KycRequestViewModel, silent = false): void {
    this.loading = true;
    
    this.service.submeterParaValidacao(payload).subscribe({
      next: (response) => {
        if (response?.sucesso) {
          this.loadData();
          if (!silent) {
            this.snack.flashMessage('Dados gravados com sucesso!', ['snackbar-success'], 'Fechar', 5000);
          }
        } else if (!silent) {
          this.snack.flashMessage(response?.mensagens?.join(', ') || 'Erro ao gravar os dados.', ['snackbar-error'], 'Fechar', 5000);
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao submeter para validação:', err);
        this.snack.flashMessage('Erro ao gravar os dados.', ['snackbar-error'], 'Fechar', 5000);
        this.loading = false;
      },
    });
  }

  submeterParaValidacao(): void {
    const payload = this.getKycPayload();

    if (!payload) {
      this.snack.flashMessage('Sessão inválida. Refaça o login.', ['snackbar-error'], 'Fechar', 5000);
      return;
    }
    this.executeSubmissao(payload);
  }

  // ==================== Utility Methods ====================
  scrollToTopAnimated(): void {
    const scrollToTop = () => {
      const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
      if (currentScroll > 0) {
        window.requestAnimationFrame(scrollToTop);
        window.scrollTo(0, 0);
      }
    };
    scrollToTop();
  }
}
